/*
  Assaboor Enterprise Theme
  Global JavaScript Foundation
  Version: 1.0.0-alpha.1
*/

(() => {
  'use strict';

  const AssaboorTheme = window.AssaboorTheme || {};

  /**
   * Safely query one element.
   */
  const getElement = (selector, scope = document) => {
    return scope.querySelector(selector);
  };

  /**
   * Safely query multiple elements.
   */
  const getElements = (selector, scope = document) => {
    return Array.from(scope.querySelectorAll(selector));
  };

  /**
   * Dispatch a reusable Assaboor theme event.
   */
  const dispatchThemeEvent = (name, detail = {}) => {
    document.dispatchEvent(
      new CustomEvent(`assaboor:${name}`, {
        bubbles: true,
        detail
      })
    );
  };

  /**
   * Trap keyboard focus inside an open drawer or modal.
   */
  const trapFocus = (container) => {
    if (!container) {
      return null;
    }

    const focusableSelector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    const focusableElements = getElements(
      focusableSelector,
      container
    ).filter((element) => {
      return !element.hasAttribute('hidden');
    });

    if (!focusableElements.length) {
      return null;
    }

    const firstElement = focusableElements[0];
    const lastElement =
      focusableElements[focusableElements.length - 1];

    const handleKeydown = (event) => {
      if (event.key !== 'Tab') {
        return;
      }

      if (
        event.shiftKey &&
        document.activeElement === firstElement
      ) {
        event.preventDefault();
        lastElement.focus();
        return;
      }

      if (
        !event.shiftKey &&
        document.activeElement === lastElement
      ) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    container.addEventListener('keydown', handleKeydown);
    firstElement.focus();

    return () => {
      container.removeEventListener(
        'keydown',
        handleKeydown
      );
    };
  };

  /**
   * Drawer manager.
   *
   * Expected markup:
   *
   * Open button:
   * data-ae-drawer-open="DrawerId"
   *
   * Drawer:
   * id="DrawerId"
   * data-ae-drawer
   *
   * Close button:
   * data-ae-drawer-close
   */
  class DrawerManager {
    constructor() {
      this.activeDrawer = null;
      this.activeTrigger = null;
      this.releaseFocusTrap = null;

      this.handleDocumentClick =
        this.handleDocumentClick.bind(this);

      this.handleKeydown =
        this.handleKeydown.bind(this);
    }

    init() {
      document.addEventListener(
        'click',
        this.handleDocumentClick
      );

      document.addEventListener(
        'keydown',
        this.handleKeydown
      );
    }

    handleDocumentClick(event) {
      const openTrigger = event.target.closest(
        '[data-ae-drawer-open]'
      );

      if (openTrigger) {
        event.preventDefault();

        const drawerId =
          openTrigger.getAttribute(
            'data-ae-drawer-open'
          );

        this.open(drawerId, openTrigger);
        return;
      }

      const closeTrigger = event.target.closest(
        '[data-ae-drawer-close]'
      );

      if (closeTrigger) {
        event.preventDefault();
        this.close();
        return;
      }

      if (
        this.activeDrawer &&
        event.target.matches('[data-ae-drawer-overlay]')
      ) {
        this.close();
      }
    }

    handleKeydown(event) {
      if (
        event.key === 'Escape' &&
        this.activeDrawer
      ) {
        this.close();
      }
    }

    open(drawerId, trigger = null) {
      const drawer = document.getElementById(drawerId);

      if (!drawer) {
        return;
      }

      if (this.activeDrawer) {
        this.close(false);
      }

      this.activeDrawer = drawer;
      this.activeTrigger = trigger;

      drawer.removeAttribute('hidden');
      drawer.setAttribute('aria-hidden', 'false');
      drawer.classList.add('is-open');

      document.documentElement.classList.add(
        'ae-drawer-open'
      );

      document.body.classList.add(
        'ae-overflow-hidden'
      );

      this.releaseFocusTrap = trapFocus(drawer);

      dispatchThemeEvent('drawer:open', {
        drawer,
        drawerId
      });
    }

    close(restoreFocus = true) {
      if (!this.activeDrawer) {
        return;
      }

      const closingDrawer = this.activeDrawer;

      closingDrawer.classList.remove('is-open');
      closingDrawer.setAttribute(
        'aria-hidden',
        'true'
      );

      window.setTimeout(() => {
        closingDrawer.setAttribute('hidden', '');
      }, 280);

      document.documentElement.classList.remove(
        'ae-drawer-open'
      );

      document.body.classList.remove(
        'ae-overflow-hidden'
      );

      if (typeof this.releaseFocusTrap === 'function') {
        this.releaseFocusTrap();
      }

      if (
        restoreFocus &&
        this.activeTrigger instanceof HTMLElement
      ) {
        this.activeTrigger.focus();
      }

      dispatchThemeEvent('drawer:close', {
        drawer: closingDrawer
      });

      this.activeDrawer = null;
      this.activeTrigger = null;
      this.releaseFocusTrap = null;
    }
  }

  /**
   * Disclosure manager.
   *
   * Expected markup:
   *
   * Trigger:
   * data-ae-disclosure-trigger="DisclosureId"
   *
   * Content:
   * id="DisclosureId"
   * data-ae-disclosure-content
   */
  class DisclosureManager {
    init() {
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest(
          '[data-ae-disclosure-trigger]'
        );

        if (!trigger) {
          return;
        }

        event.preventDefault();

        const disclosureId =
          trigger.getAttribute(
            'data-ae-disclosure-trigger'
          );

        const content =
          document.getElementById(disclosureId);

        if (!content) {
          return;
        }

        const isExpanded =
          trigger.getAttribute('aria-expanded') ===
          'true';

        trigger.setAttribute(
          'aria-expanded',
          String(!isExpanded)
        );

        content.hidden = isExpanded;
        content.classList.toggle(
          'is-open',
          !isExpanded
        );

        dispatchThemeEvent('disclosure:toggle', {
          trigger,
          content,
          expanded: !isExpanded
        });
      });
    }
  }

  /**
   * Quantity selector manager.
   *
   * Expected markup:
   *
   * Wrapper:
   * data-ae-quantity
   *
   * Input:
   * data-ae-quantity-input
   *
   * Buttons:
   * data-ae-quantity-minus
   * data-ae-quantity-plus
   */
  class QuantityManager {
    init() {
      document.addEventListener('click', (event) => {
        const minusButton = event.target.closest(
          '[data-ae-quantity-minus]'
        );

        const plusButton = event.target.closest(
          '[data-ae-quantity-plus]'
        );

        if (!minusButton && !plusButton) {
          return;
        }

        event.preventDefault();

        const button = minusButton || plusButton;

        const wrapper = button.closest(
          '[data-ae-quantity]'
        );

        if (!wrapper) {
          return;
        }

        const input = getElement(
          '[data-ae-quantity-input]',
          wrapper
        );

        if (!input) {
          return;
        }

        const minimum =
          Number.parseInt(input.min, 10) || 1;

        const maximum =
          Number.parseInt(input.max, 10) ||
          Number.POSITIVE_INFINITY;

        const step =
          Number.parseInt(input.step, 10) || 1;

        const currentValue =
          Number.parseInt(input.value, 10) ||
          minimum;

        let nextValue = currentValue;

        if (plusButton) {
          nextValue = Math.min(
            currentValue + step,
            maximum
          );
        }

        if (minusButton) {
          nextValue = Math.max(
            currentValue - step,
            minimum
          );
        }

        input.value = nextValue;

        input.dispatchEvent(
          new Event('change', {
            bubbles: true
          })
        );

        dispatchThemeEvent('quantity:change', {
          input,
          value: nextValue
        });
      });

      document.addEventListener('change', (event) => {
        const input = event.target.closest(
          '[data-ae-quantity-input]'
        );

        if (!input) {
          return;
        }

        const minimum =
          Number.parseInt(input.min, 10) || 1;

        const maximum =
          Number.parseInt(input.max, 10) ||
          Number.POSITIVE_INFINITY;

        let value =
          Number.parseInt(input.value, 10) ||
          minimum;

        value = Math.max(minimum, value);
        value = Math.min(maximum, value);

        input.value = value;
      });
    }
  }

  /**
   * Dismissible elements.
   *
   * Trigger:
   * data-ae-dismiss
   *
   * Parent:
   * data-ae-dismissible
   */
  class DismissibleManager {
    init() {
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest(
          '[data-ae-dismiss]'
        );

        if (!trigger) {
          return;
        }

        const dismissible = trigger.closest(
          '[data-ae-dismissible]'
        );

        if (!dismissible) {
          return;
        }

        event.preventDefault();

        dismissible.classList.add('is-hidden');

        window.setTimeout(() => {
          dismissible.remove();
        }, 280);

        dispatchThemeEvent('dismiss', {
          element: dismissible
        });
      });
    }
  }

  /**
   * Lazy animation observer.
   *
   * Elements can use:
   * data-ae-reveal
   */
  class RevealManager {
    constructor() {
      this.observer = null;
    }

    init() {
      const elements = getElements(
        '[data-ae-reveal]'
      );

      if (!elements.length) {
        return;
      }

      const reducedMotion =
        window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        ).matches;

      if (
        reducedMotion ||
        !('IntersectionObserver' in window)
      ) {
        elements.forEach((element) => {
          element.classList.add('is-visible');
        });

        return;
      }

      this.observer = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              return;
            }

            entry.target.classList.add(
              'is-visible'
            );

            observer.unobserve(entry.target);
          });
        },
        {
          rootMargin: '0px 0px -8% 0px',
          threshold: 0.12
        }
      );

      elements.forEach((element) => {
        this.observer.observe(element);
      });
    }
  }

  /**
   * Loading-state utility.
   */
  const setLoadingState = (
    element,
    isLoading = true
  ) => {
    if (!element) {
      return;
    }

    element.classList.toggle(
      'ae-loading',
      isLoading
    );

    element.setAttribute(
      'aria-busy',
      String(isLoading)
    );

    if (
      element instanceof HTMLButtonElement ||
      element instanceof HTMLInputElement
    ) {
      element.disabled = isLoading;
    }
  };

  /**
   * Safe JSON response helper.
   */
  const parseJsonResponse = async (response) => {
    const contentType =
      response.headers.get('content-type') || '';

    if (
      contentType.includes('application/json')
    ) {
      return response.json();
    }

    return {
      message: await response.text()
    };
  };

  /**
   * Safe fetch helper for future Shopify AJAX requests.
   */
  const request = async (
    url,
    options = {}
  ) => {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...options.headers
      },
      ...options
    });

    const data =
      await parseJsonResponse(response);

    if (!response.ok) {
      const error = new Error(
        data.description ||
        data.message ||
        'The request could not be completed.'
      );

      error.status = response.status;
      error.data = data;

      throw error;
    }

    return data;
  };

  /**
   * Reinitialize dynamic elements after Shopify
   * Theme Editor section updates.
   */
  const handleShopifySectionLoad = () => {
    const revealManager = new RevealManager();
    revealManager.init();

    dispatchThemeEvent('section:load');
  };

  /**
   * Theme initialization.
   */
  const initializeTheme = () => {
    const drawerManager = new DrawerManager();
    const disclosureManager =
      new DisclosureManager();
    const quantityManager =
      new QuantityManager();
    const dismissibleManager =
      new DismissibleManager();
    const revealManager =
      new RevealManager();

    drawerManager.init();
    disclosureManager.init();
    quantityManager.init();
    dismissibleManager.init();
    revealManager.init();

    AssaboorTheme.drawerManager =
      drawerManager;

    AssaboorTheme.utils = {
      getElement,
      getElements,
      request,
      setLoadingState,
      trapFocus,
      dispatchThemeEvent
    };

    window.AssaboorTheme = AssaboorTheme;

    dispatchThemeEvent('ready');
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initializeTheme,
      {
        once: true
      }
    );
  } else {
    initializeTheme();
  }

  document.addEventListener(
    'shopify:section:load',
    handleShopifySectionLoad
  );
})();